/* The row of numbers at the top of a panel. One component used by every page, but never with
 * the same numbers: the Overview owns the site-wide row (see `StatTiles`) and every other
 * panel builds its own from the data it already fetched.
 *
 * A tile given a `to` becomes the way into the panel that explains it, so the Overview can be
 * read as a map: the figure that looks wrong is also the link to where it is broken down.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@nextleap-al/admin-ui';

export interface TileSpec {
  label: string;
  value: string;
  /** Relative change against the preceding window, where a panel has one. */
  delta?: number | null;
  hint?: string;
  /** Escalation, weak evidence and failure: down is the good direction. */
  lowerIsBetter?: boolean;
  /**
   * Colours the figure for a number that is not merely high but wrong. Never the only
   * carrier of the meaning — the hint beside it has to say the same thing in words.
   */
  tone?: 'critical' | 'warning';
  /** Decorative mark for the figure. The label is what names it; this only helps find it. */
  icon?: ReactNode;
  /** The panel this figure is broken down on. Set, and the whole tile is a link to it. */
  to?: string;
  /** That panel's name, spelled as the rail spells it for this account. */
  toLabel?: string;
}

const TONE = {
  critical: 'var(--chart-critical)',
  warning: 'var(--chart-warning)',
} as const;

/**
 * One figure, its label, its movement and a sentence saying what it counts. The arrow and
 * percentage always describe how the measure moved; only the colour says whether that was
 * the good direction — and the sentence underneath is never dropped for it, because a
 * caveat about what the number excludes does not stop applying in a week where it moved.
 */
export function Tile({ label, value, delta, hint, lowerIsBetter, tone, icon, to, toLabel }: TileSpec) {
  const moved = delta != null && Number.isFinite(delta) && Math.abs(delta) > 0.001;
  const rose = (delta ?? 0) > 0;
  const good = lowerIsBetter ? !rose : rose;
  const deltaColour = `var(${good ? '--chart-good' : '--chart-critical'})`;

  const body = (
    <>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 text-xs font-medium uppercase tracking-wide text-[var(--text-tertiary)]">
          {label}
        </div>
        {icon && (
          <span
            aria-hidden="true"
            className="flex h-7 w-7 flex-none items-center justify-center rounded-[var(--radius-md)] bg-[var(--primary-light)] text-[var(--primary)]"
          >
            {icon}
          </span>
        )}
      </div>

      <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span
          className="text-2xl font-semibold tabular-nums text-[var(--text-primary)]"
          style={tone ? { color: TONE[tone] } : undefined}
        >
          {value}
        </span>
        {moved && (
          <span
            className="rounded-[var(--radius-full)] px-1.5 py-0.5 text-xs font-semibold tabular-nums"
            style={{
              color: deltaColour,
              background: `color-mix(in srgb, ${deltaColour} 14%, transparent)`,
            }}
          >
            {rose ? '↑' : '↓'} {Math.abs((delta as number) * 100).toFixed(0)}%
          </span>
        )}
      </div>

      <div className="mt-1 text-xs leading-snug text-[var(--text-muted)]">
        {hint || (moved ? 'vs the previous period' : ' ')}
      </div>
    </>
  );

  if (!to) {
    return (
      <Card padding="sm" className="flex h-full min-w-0 flex-col">
        {body}
      </Card>
    );
  }

  return (
    <Link
      to={to}
      className="group block min-w-0 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-secondary)]"
    >
      <Card hover padding="sm" className="flex h-full min-w-0 flex-col">
        {body}
        {/* Not printed on the tile — the lift under the pointer is the affordance there —
            but a link whose destination is only a hover state is no destination at all to
            anybody reading this with a screen reader. */}
        <span className="sr-only">{toLabel ? `Open ${toLabel}` : 'Open the detail'}</span>
      </Card>
    </Link>
  );
}

/** Written out rather than interpolated: Tailwind reads the class names as source text and
 *  a computed `md:grid-cols-${n}` never reaches the stylesheet. */
const COLUMNS: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
};

/**
 * The tiles as a row, filling it. The count decides the columns, so a panel with three does
 * not leave an empty slot that reads as a card which failed to load.
 */
export function TileRow({ tiles }: { tiles: TileSpec[] }) {
  if (!tiles.length) return null;
  return (
    <div className={`grid grid-cols-2 gap-3 ${COLUMNS[tiles.length] ?? 'md:grid-cols-4'}`}>
      {tiles.map((tile) => (
        <Tile key={tile.label} {...tile} />
      ))}
    </div>
  );
}
